import localObject from "utilities/localObject";
import {
  selectCombinationsForLegCount,
  selectPlaceableMultiStake,
} from "@/features/betslip/stores/computed";
import type {
  BetslipStoreHandle,
  LastSubmitted,
} from "@/features/betslip/services/submitBet/types";

const lastBetslipKey = (userId: string) =>
  `pb-last-betslip-submitted-${userId}`;

export const readLastSubmitted = (
  userId: string | undefined,
): LastSubmitted | null => {
  if (!userId) return null;
  const [value] = localObject.getValue<LastSubmitted>(lastBetslipKey(userId));
  return value ?? null;
};

const buildCombinationStakes = (
  store: ReturnType<BetslipStoreHandle["getState"]>,
): Record<string, number> => {
  const totals: Record<string, number> = {};
  for (const [size, combo] of Object.entries(
    selectCombinationsForLegCount(store),
  )) {
    if (combo.total > 0) totals[size] = combo.total;
  }
  return totals;
};

/**
 * The slip composition the duplicate guard compares on: every selection
 * and every stake behind it. Both the persisted record and the slip being
 * submitted are built here so the guard can't compare fewer fields than
 * it stores. Stakes are keyed off the selections rather than the stakes
 * map, so an unstaked leg reads as 0 on both sides instead of a missing
 * key.
 */
export const buildSubmissionSnapshot = (
  store: ReturnType<BetslipStoreHandle["getState"]>,
): LastSubmitted => {
  const outcomeIds = Object.keys(store.selections);
  return {
    outcomeIds,
    singleStakes: Object.fromEntries(
      outcomeIds.map((outcomeId) => [
        outcomeId,
        store.stakes.singles[outcomeId] ?? 0,
      ]),
    ),
    // What a submit would send, so re-forming a parked multi isn't a dupe.
    multiStake: selectPlaceableMultiStake(store),
    // Only combos that can form and hold a stake. The fingerprint compares key
    // counts, so a zeroed or orphaned record stops an identical slip matching.
    combinationStakes: buildCombinationStakes(store),
  };
};

/**
 * Snapshot the current slip composition so the next submit can detect
 * a duplicate.
 */
export const writeLastSubmitted = (
  userId: string | undefined,
  store: ReturnType<BetslipStoreHandle["getState"]>,
): void => {
  if (!userId) return;
  localObject.setValue(lastBetslipKey(userId), buildSubmissionSnapshot(store));
};
